import { CrudStoreItemPartner360 } from 'legacy/lut/classes/models/partner360/CrudStoreItemPartner360'
import { IFormMarketShare } from 'legacy/lut/classes/models/partner360/IFormMarketShare'
import { useEffect, useState } from 'react'

import { IDataProviderFilter, IFilter, Logic, Operator } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { CrudStore } from 'core/types/crudStore'

export interface IUseMarketShare {
  partnerId: string
  fyear: number
  programType: string
  section: MarketShareSections
  showPreviousYears?: number
}

export type MarketShareSections =
  | ''
  | 'jbp-gtm-priorities'
  | 'jbp-total-business'
  | 'jbp-consumer-electronics'

const SHOW_PREVIOUS_YEARS = 3

export const useMarketShare = ({
  partnerId,
  fyear,
  programType,
  section,
  showPreviousYears = SHOW_PREVIOUS_YEARS,
}: IUseMarketShare) => {
  const [marketShareData, setMarketShareData] = useState<IFormMarketShare[]>([])
  const [marketShareReserved, setMarketShareReserved] = useState<IFormMarketShare[]>([])
  const [isMarketShareChanged, setIsMarketShareChanged] = useState(false)
  const [isMarketShareLoaded, setIsMarketShareLoaded] = useState(false)

  const marketShareCrud = CrudStore.get(
    CrudStoreItemPartner360.JBPMarketShare
  ) as Crud<IFormMarketShare>

  const fetchMarketShare = async (reload = false) => {
    if ((partnerId && fyear && programType) || reload) {
      setIsMarketShareLoaded(false)

      if (!reload) {
        const filters: (IFilter | IDataProviderFilter)[] = [
          {
            field: 'PartnerId',
            operator: Operator.EQUAL,
            value: partnerId,
          },
          {
            field: 'FYear',
            operator: Operator.GTE,
            value: fyear - showPreviousYears,
          },
          {
            field: 'FYear',
            operator: Operator.LTE,
            value: fyear,
          },
          {
            field: 'ProgramType',
            operator: Operator.EQUAL,
            value: programType,
          },
        ]

        if (section) {
          filters.push({
            field: 'Section',
            operator: Operator.EQUAL,
            value: section,
          })
        } else {
          filters.push({
            logic: Logic.OR,
            filters: [
              {
                field: 'Section',
                operator: Operator.ISNULL,
                value: '',
              },
              {
                field: 'Section',
                operator: Operator.ISEMPTY,
                value: '',
              },
            ],
          })
        }

        marketShareCrud.setFilter({
          logic: Logic.AND,
          filters: filters,
        })
      }

      //metrics.loadDataStart('MarketShare')
      const result = await marketShareCrud.fetch()
      //metrics.loadDataStop('MarketShare')

      const resultData = result && result.items ? result.items : []

      const data: IFormMarketShare[] = []
      const reserved: IFormMarketShare[] = []

      for (let i = showPreviousYears; i >= 0; i--) {
        const oneYearData = resultData.find((el) => el.FYear === fyear - i)
        const emptyData: IFormMarketShare = {
          EntryId: 0,
          PartnerId: partnerId,
          FYear: fyear - i,
          ProgramType: programType,

          Section: section,

          AllPartnersTotal: 0,
          HPPartnersTotal: 0,

          PercentageValue: null,
        }

        const percentageValue = (oneYearData || {}).PercentageValue
        const isPercentageValueExist = !!percentageValue || percentageValue == 0
        const minPercentageValue = Math.min(percentageValue || 0, 100)
        const calculatedValue = isPercentageValueExist ? 100 - minPercentageValue : null

        const marketShareitem = {
          ...(oneYearData ? oneYearData : emptyData),
          ['CalculatedValue']: calculatedValue,
        }

        data.push(marketShareitem)
        reserved.push(marketShareitem)
      }

      setMarketShareData(data)
      setMarketShareReserved(reserved)
      setIsMarketShareChanged(false)

      setIsMarketShareLoaded(true)
    }
  }

  useEffect(() => {
    fetchMarketShare()
  }, [partnerId, fyear, programType])

  useEffect(() => {
    return marketShareCrud.subscribe('change', (changedData: any) => {
      if (changedData && changedData.length) {
        fetchMarketShare(true)
      }
    })
  }, [partnerId, fyear, programType])

  return {
    marketShareData,
    marketShareReserved,
    isMarketShareChanged,
    isMarketShareLoaded,
  }
}
